import { Injectable, Inject, forwardRef } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { BehaviorSubject, Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import {
  Exercise,
  exerciseQuestionFromJson,
  ExerciseQuestionType,
  IExercise,
  IExerciseQuestionType,
  DialogData,
  DialogExerciseQuestion,
} from "./exercises.interface";
import { Task } from "./task";
import { Answers } from "./answers";
import { UserService } from "./user.service";
import { User } from "./user";
import { AuthService } from "./auth.service";

/**
 * Service that holds the exercises.
 */
@Injectable({
  providedIn: "root",
})
export class ExerciseService {
  public static storedExercise?: Exercise;

  public exercises: Array<Exercise> = [];

  public exerciseQuestions: Array<ExerciseQuestionType> = [];

  public tags: Array<string> = [];
   
  public activeExercise?: Exercise;

  public isInitialized = new BehaviorSubject(false);

  private correctAnswers = 0;

  public tasks: Array<Task> = [];

  private isInitializing = false;

  private destroy$ = new Subject<void>();

  public constructor(
    private httpClient: HttpClient,
    private auth: AuthService,
    @Inject(forwardRef(() => UserService)) private userService: UserService
  ) {
    this.userService.user$
    .pipe(takeUntil(this.destroy$))
    .subscribe((user: User) => {
      const hasBackendUser = this.userService.isLoggedIn(user);
      const hasNativeUser  = !hasBackendUser && !!this.auth.fireUser && !this.auth.fireUser.isAnonymous;

      if (hasBackendUser || hasNativeUser) {
        // Optional: gegen Mehrfach-Init schützen:
        if (!this.isInitialized.value) {
          this.initializeService();
        }
      } else {
        this.exercises = [];
        this.exerciseQuestions = [];
        this.tags = [];
        this.tasks = [];
        this.isInitialized.next(false);
      }
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private async initializeService(): Promise<void> {
    if (this.isInitializing) return;
    
    this.isInitializing = true;
    try {
      await Promise.all([this.getAllExercises(), this.getAllTags(), this.getAllQuestions(), this.getAllTasks()]);
      this.isInitialized.next(true);
    } catch (error) {
      console.error('Error initializing ExerciseService:', error);
      // Retry after a delay if it's an authentication error
      if (error.status === 403 || error.status === 401) {
        setTimeout(() => {
          this.isInitializing = false;
          this.initializeService();
        }, 2000);
      }
    } finally {
      this.isInitializing = false;
    }
  }

  public async getAllExercises(): Promise<void> {
    const rawExercises = await this.httpClient.get<Array<IExercise>>("exercise").toPromise();
    console.log('[ExerciseService] Raw exercises from API:', rawExercises);
    
    // Log VoiceDialog exercises specifically
    const voiceDialogExercises = rawExercises?.filter(e => (e as any).type === 'VoiceDialog');
    if (voiceDialogExercises && voiceDialogExercises.length > 0) {
      console.log('[ExerciseService] VoiceDialog exercises found:', voiceDialogExercises.map(e => ({
        _id: e._id,
        scenario: (e as any).scenario,
        goal: (e as any).goal,
        aiRole: (e as any).aiRole,
        exerciseData: (e as any).exerciseData
      })));
    }
    
    this.exercises = rawExercises.map((e) => this.transformExerciseFromBackend(e));
  }

  public async getAllStudentExercises(): Promise<void> {
    try {
      // Load both standard exercises and student exercises
      const [standardExercises, studentExercises] = await Promise.all([
        this.httpClient.get<Array<IExercise>>("exercise").toPromise(),
        this.httpClient.get<Array<IExercise>>("exercise/student").toPromise()
      ]);
      
      console.log('[ExerciseService] Raw student exercises from API:', studentExercises);
      
      // Log VoiceDialog exercises specifically from student endpoint
      const voiceDialogExercises = studentExercises?.filter(e => (e as any).type === 'VoiceDialog');
      if (voiceDialogExercises && voiceDialogExercises.length > 0) {
        console.log('[ExerciseService] VoiceDialog exercises from student endpoint:', voiceDialogExercises.map(e => ({
          _id: e._id,
          scenario: (e as any).scenario,
          goal: (e as any).goal,
          aiRole: (e as any).aiRole,
          exerciseData: (e as any).exerciseData
        })));
      }
      
      // Combine both arrays
      const allExercises = [
        ...(standardExercises || []),
        ...(studentExercises || [])
      ];
      
      this.exercises = allExercises.map((e) => this.transformExerciseFromBackend(e));
      console.log('Loaded student exercises:', {
        standard: standardExercises?.length || 0,
        student: studentExercises?.length || 0,
        total: this.exercises.length
      });
    } catch (error) {
      console.error('Error loading student exercises:', error);
      // Fallback: try to load with a timeout
      try {
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Request timeout')), 10000)
        );
        
        // Try to load both types of exercises with timeout
        const dataPromise = Promise.all([
          this.httpClient.get<Array<IExercise>>("exercise").toPromise(),
          this.httpClient.get<Array<IExercise>>("exercise/student").toPromise()
        ]);
        
        const result = await Promise.race([dataPromise, timeoutPromise]);
        const [standardExercises, studentExercises] = result as [Array<IExercise>, Array<IExercise>];
        
        const allExercises = [
          ...(standardExercises || []),
          ...(studentExercises || [])
        ];
        
        this.exercises = allExercises.map((e) => this.transformExerciseFromBackend(e));
      } catch (fallbackError) {
        console.error('Fallback loading student exercises failed:', fallbackError);
        // Keep existing exercises if any
        if (this.exercises.length === 0) {
          throw fallbackError;
        }
      }
    }
  }

  public async returnAllStudentExercises(): Promise<Exercise[]> {
    return (await this.httpClient.get<Array<IExercise>>("exercise/student").toPromise());
  }

  public async returnAllExercises(): Promise<Exercise[]> {
    return (await this.httpClient.get<Array<IExercise>>("exercise").toPromise()).map((e) => new Exercise(e));
  }

  public getExerciseArray() {
    return this.exercises;
  }

  public async getAllQuestions(): Promise<void> {
    this.exerciseQuestions = (await this.httpClient.get<Array<IExerciseQuestionType>>("exercise/question").toPromise())
      .map((e) => exerciseQuestionFromJson(e));
  }

  public async createExercise(exercise: IExercise): Promise<void> {
    this.exercises.push(new Exercise(await this.httpClient.post<IExercise>("exercise", exercise).toPromise()));
  }

  public async updateExercise(exercise: IExercise): Promise<void> {
    this.exercises.splice(this.exercises.findIndex((e) => e._id === exercise._id), 1);
    this.exercises.push(new Exercise(await this.httpClient.put<IExercise>(`exercise/${exercise._id}`, exercise).toPromise()));
  }

  public async createQuestions(questions: IExerciseQuestionType[]): Promise<void> {
    const results = [];
    for (const question of questions) {
      results.push(this.httpClient.post<IExerciseQuestionType>("exercise/question", question).toPromise());
    }
    await Promise.all(results);
    await this.getAllExercises();
  }

  public async updateQuestions(question: IExerciseQuestionType): Promise<void> {
    await this.httpClient.put<IExerciseQuestionType>(`exercise/question/${question._id}`, question).toPromise();
    await this.getAllExercises();
  }

  public async getExerciseWithId(id: string): Promise<Exercise> {
    if(this.exercises.length == 0)
    {
      await this.getAllExercises();
    }
    return this.exercises.find((e) => e._id === id);
  }

  public async getQuestionWithId(id: string): Promise<ExerciseQuestionType | undefined > {
    return this.exerciseQuestions.find((q) => q._id === id);
  }

  private async getAllTags(): Promise<void> {
    this.tags = (await this.httpClient.get<Array<string>>("exercise/tags").toPromise());
    console.log(this.tags);
  }

  public clearCurrExercise(): void {
    if (typeof this.activeExercise !== "undefined") {
      this.activeExercise = undefined;
    }
  }

  /*public getProgress(skills: Array<Skill>, exerciseid: string): number
  {
      let skill = skills.find(skill => skill.exerciseid === exerciseid);
      if(skill)
      {
          let neededXP = 0;
          let currentLevel = skill.level!;
          let toadd = 15 + (currentLevel * 5);
          if(toadd <= 100)
          {
              neededXP = toadd;
          }
          else {
              neededXP = 100;
          }
          return (skill.xp! / neededXP);
      }
      else {
          return 0;
      }
  }*/

  public getCorrectAnswers(results: Array<IExerciseQuestionType | Answers>): number {
    this.correctAnswers = 0;
    results.forEach(res => {
      if (res.isCorrect) {
        this.correctAnswers++;
      }
    });
    return this.correctAnswers;
  }

  public async getTaskById(id: string): Promise<Task | undefined > {
    return this.tasks.find((t) => t._id === id);
  }

  public async getTaskByTag(tag: string): Promise<Task | undefined> {
    await this.getAllTasks();
    let foundtask = undefined;

    for(let task of this.tasks)
    {
      for(let i = 0; i < task.tags.length; i++)
      {
        if(task.tags[i] == tag)
        {
          foundtask = task;
          break;
        }
      }
    }
    return foundtask;
  }

  public async createTask(task: Task): Promise<void> {
    this.tasks.push(await this.httpClient.post<Task>("exercise/task", task).toPromise());
  }

  public async updateTask(task: Task): Promise<void> {
    this.tasks.splice(this.tasks.findIndex((t) => t._id === task._id), 1);
    this.tasks.push((await this.httpClient.put<Task>(`exercise/task/${task._id}`, task).toPromise()));
  }

  public async getAllTasks(): Promise<void> {
    this.tasks = (await this.httpClient.get<Array<Task>>("exercise/tasks").toPromise());
  }

  async getExercisesForStudent(): Promise<Exercise[]> {
    return await this.httpClient.get<Exercise[]>(`assignments/student`).toPromise();
  }

  async getTeacherExercisesByAuthor(): Promise<Exercise[]> {
    try {
      // Load both standard exercises and teacher exercises
      const [standardExercises, teacherExercises] = await Promise.all([
        this.httpClient.get<Array<IExercise>>("exercise").toPromise(),
        this.httpClient.get<Array<IExercise>>(`content/library`).toPromise()
      ]);
      
      // Combine both arrays
      const allExercises = [
        ...(standardExercises || []),
        ...(teacherExercises || [])
      ];
      
      // Store all exercises in the exercises array
      this.exercises = allExercises.map((e) => this.transformExerciseFromBackend(e));
      
      // Trigger initialization to notify subscribers that exercises are loaded
      this.isInitialized.next(true);
      
      return allExercises.map((e) => this.transformExerciseFromBackend(e));
    } catch (error) {
      console.error('Error loading teacher exercises by author:', error);
      return [];
    }
  }

  async getOnlyTeacherExercises(): Promise<Exercise[]> {
    try {
      // Load only teacher exercises from content library
      const teacherExercises = await this.httpClient.get<Array<IExercise>>(`content/library`).toPromise();
      return teacherExercises ? teacherExercises.map((e) => this.transformExerciseFromBackend(e)) : [];
    } catch (error) {
      console.error('Error loading only teacher exercises:', error);
      return [];
    }
  }

  /**
   * Map backend difficulty level to frontend difficulty level
   */
  private mapDifficultyLevel(level: string): 1 | 2 | 3 {
    switch (level) {
      case 'A1':
        return 1;
      case 'A2':
        return 1;
      case 'B1':
        return 2;
      case 'B2':
        return 2;
      case 'C1':
        return 3;
      default:
        return 1;
    }
  }

  /**
   * Transform exercise from backend format to frontend format
   */
  private transformExerciseFromBackend(exercise: IExercise | any): Exercise {
    // Handle pure VoiceDialog configs without dialog/meta (from early saves)
    if (exercise.type === 'VoiceDialog' && (!exercise.dialog || !Array.isArray(exercise.dialog))) {
      // VoiceDialog exercises don't need dialog/meta transformation, they use scenario/goal/aiRole directly
      // Just ensure questions array is empty if not present
      if (!exercise.questions) {
        exercise.questions = [];
      }
      
      console.log('[ExerciseService] Transforming VoiceDialog exercise:', {
        _id: exercise._id,
        scenario: exercise.scenario,
        goal: exercise.goal,
        aiRole: exercise.aiRole,
        level: exercise.level,
        exerciseData: exercise.exerciseData,
        fullExercise: exercise
      });
      
      // Preserve VoiceDialog-specific fields for later use
      const mapped = new Exercise({
        _id: exercise._id,
        title: exercise.title || exercise.scenario || 'Voice Dialog',
        description: exercise.description || exercise.goal || '',
        questions: [],
        tags: exercise.tags || [],
        maxProgress: 0,
        difficulty: this.mapDifficultyLevel(exercise.level || 'B1'),
        author: exercise.author
      });
      
      // Preserve VoiceDialog meta for UI - IMPORTANT: preserve ALL data sources
      // Use top-level values first, then fall back to exerciseData if needed
      (mapped as any).type = 'VoiceDialog';
      
      // Extract with explicit checks to ensure we get the top-level values
      // ALWAYS prioritize top-level fields over exerciseData fields
      console.log('[ExerciseService] Raw exercise data for transformation:', {
        hasAiRole: !!exercise.aiRole,
        hasScenario: !!exercise.scenario,
        hasGoal: !!exercise.goal,
        hasLevel: !!exercise.level,
        aiRole: exercise.aiRole,
        scenario: exercise.scenario,
        goal: exercise.goal,
        level: exercise.level,
        hasExerciseData: !!exercise.exerciseData,
        exerciseDataAiRole: (exercise.exerciseData as any)?.aiRole,
        exerciseDataScenario: (exercise.exerciseData as any)?.scenario,
        exerciseDataGoal: (exercise.exerciseData as any)?.goal
      });
      
      const topLevelAiRole = exercise.aiRole && typeof exercise.aiRole === 'string' && exercise.aiRole.trim().length > 0 
        ? exercise.aiRole.trim() 
        : null;
      const topLevelScenario = exercise.scenario && typeof exercise.scenario === 'string' && exercise.scenario.trim().length > 0 
        ? exercise.scenario.trim() 
        : null;
      const topLevelGoal = exercise.goal && typeof exercise.goal === 'string' && exercise.goal.trim().length > 0 
        ? exercise.goal.trim() 
        : null;
      const topLevelLevel = exercise.level && typeof exercise.level === 'string' && exercise.level.trim().length > 0 
        ? exercise.level.trim() 
        : null;
      
      console.log('[ExerciseService] Extracted top-level values:', {
        topLevelAiRole,
        topLevelScenario,
        topLevelGoal,
        topLevelLevel
      });
      
      // Calculate final values - use top-level values if available, otherwise fall back to exerciseData
      const finalAiRole = topLevelAiRole || (exercise.exerciseData as any)?.aiRole || '';
      const finalScenario = topLevelScenario 
        || (exercise.theme && typeof exercise.theme === 'string' && exercise.theme.trim().length > 0 ? exercise.theme.trim() : null) 
        || (exercise.exerciseData as any)?.scenario || '';
      const finalGoal = topLevelGoal 
        || (exercise.description && typeof exercise.description === 'string' && exercise.description.trim().length > 0 ? exercise.description.trim() : null) 
        || (exercise.exerciseData as any)?.goal || '';
      const finalLevel = topLevelLevel || (exercise.exerciseData as any)?.level || 'B1';
      const finalMaxTurns = exercise.maxTurns || (exercise.exerciseData as any)?.maxTurns || 20;
      const finalMilestones = exercise.milestones || (exercise.exerciseData as any)?.milestones || [];
      const finalEvaluationCriteria = exercise.evaluationCriteria || (exercise.exerciseData as any)?.evaluationCriteria || [];
      
      // Preserve the full exerciseData object and merge top-level fields into it
      const exerciseDataObj = exercise.exerciseData || {};
      if (!exerciseDataObj.scenario && finalScenario) {
        exerciseDataObj.scenario = finalScenario;
      }
      if (!exerciseDataObj.goal && finalGoal) {
        exerciseDataObj.goal = finalGoal;
      }
      if (!exerciseDataObj.aiRole && finalAiRole) {
        exerciseDataObj.aiRole = finalAiRole;
      }
      if (!exerciseDataObj.level && finalLevel) {
        exerciseDataObj.level = finalLevel;
      }
      
      console.log('[ExerciseService] Final values to be set:', {
        finalAiRole,
        finalScenario,
        finalGoal,
        finalLevel,
        finalMaxTurns,
        exerciseDataObj
      });
      
      // Set type (not part of Exercise class, so use any)
      (mapped as any).type = 'VoiceDialog';
      
      // Set VoiceDialog-specific properties directly on mapped object
      // These will be preserved by the Exercise class constructor
      mapped.aiRole = finalAiRole;
      mapped.scenario = finalScenario;
      mapped.goal = finalGoal;
      mapped.level = finalLevel;
      mapped.maxTurns = finalMaxTurns;
      mapped.milestones = finalMilestones;
      mapped.evaluationCriteria = finalEvaluationCriteria;
      mapped.exerciseData = exerciseDataObj;
      
      console.log('[ExerciseService] Transformed VoiceDialog exercise:', {
        topLevelValues: {
          aiRole: topLevelAiRole,
          scenario: topLevelScenario,
          goal: topLevelGoal,
          level: topLevelLevel
        },
        mappedValues: {
          scenario: mapped.scenario,
          goal: mapped.goal,
          aiRole: mapped.aiRole,
          level: mapped.level
        },
        exerciseData: mapped.exerciseData,
        fullMappedObject: JSON.stringify(mapped, null, 2)
      });
      
      // Ensure the fields are accessible by logging them directly
      console.log('[ExerciseService] Direct access check:', {
        'mapped.scenario': mapped.scenario,
        'mapped.goal': mapped.goal,
        'mapped.aiRole': mapped.aiRole,
        'mapped.level': mapped.level,
        'mapped.exerciseData': mapped.exerciseData
      });
      
      return mapped;
    }
    
    // Handle pure DialogInteractive configs without canonical dialog/meta (from early saves and new generator)
    if (exercise.type === 'DialogInteractive' && (!exercise.dialog || (Array.isArray(exercise.dialog) && exercise.dialog.length === 0))) {
      const directData = exercise.exerciseData || {};
      const nestedData = directData?.exerciseData || {};
      const optimization = nestedData?.optimizationApplied || {};
      const optimizedInputs = optimization?.optimizedInputs || {};
      const originalInputs = optimization?.originalInputs || {};

      const pickString = (...values: any[]) => {
        for (const value of values) {
          if (typeof value === 'string' && value.trim().length > 0) {
            return value.trim();
          }
        }
        return '';
      };

      const scenario = pickString(
        exercise.scenario,
        exercise.theme,
        directData.scenario,
        optimizedInputs.scenario,
        originalInputs.scenario,
        nestedData.scenario
      ) || exercise.title || 'Dialog';

      const goal = pickString(
        exercise.goal,
        directData.goal,
        optimizedInputs.goal,
        originalInputs.goal,
        nestedData.goal,
        exercise.description
      );

      const aiRole = pickString(
        exercise.aiRole,
        directData.aiRole,
        optimizedInputs.aiRole,
        originalInputs.aiRole,
        nestedData.aiRole
      ) || 'Tutor';

      const level = pickString(
        exercise.level,
        directData.level,
        nestedData.level
      ) || 'B1';

      const maxTurns = exercise.maxTurns || directData.maxTurns || nestedData.maxTurns || 20;
      const evaluationCriteria = exercise.evaluationCriteria || directData.evaluationCriteria || nestedData.evaluationCriteria || [];
      const milestones = exercise.milestones || directData.milestones || nestedData.milestones || [];

      const greetingText = `Hallo! ${scenario}. Lass uns beginnen.`;

      const dialogData: DialogData = {
        title: exercise.title || scenario,
        level: level || 'A1',
        language: exercise.language || 'de',
        messages: [
          {
            speaker: aiRole || 'Tutor',
            text: greetingText,
            translation: '',
            audioUrl: ''
          }
        ],
        quizzes: []
      };

      const dialogQuestion = new DialogExerciseQuestion(
        exercise._id,
        dialogData,
        -1,
        '',
        this.mapDifficultyLevel(exercise.level || 'A1'),
        exercise.tags || [],
        undefined,
        exercise.task
      );

      const mapped = new Exercise({
        _id: exercise._id,
        title: exercise.title || scenario,
        description: exercise.goal || '',
        questions: [dialogQuestion],
        tags: exercise.tags || [],
        maxProgress: 0,
        difficulty: this.mapDifficultyLevel(exercise.level || 'A1'),
        author: exercise.author
      });
      // Preserve interactive meta for UI
      (mapped as any).aiRole = aiRole;
      (mapped as any).scenario = scenario;
      (mapped as any).goal = goal;
      (mapped as any).level = level;
      (mapped as any).maxTurns = maxTurns;
      (mapped as any).milestones = milestones;
      (mapped as any).evaluationCriteria = evaluationCriteria;
      (mapped as any).exerciseData = {
        ...directData,
        scenario,
        goal,
        aiRole,
        level,
        maxTurns,
        evaluationCriteria,
        milestones,
        exerciseData: {
          ...nestedData,
          scenario: nestedData.scenario || scenario,
          goal: nestedData.goal || goal,
          aiRole: nestedData.aiRole || aiRole
        }
      };
      return mapped;
    }
    // Normalize possible shapes from userContent for interactive dialogs
    if ((exercise.type === 'Dialog' || exercise.type === 'dialog' || exercise.type === 'DialogInteractive' || exercise.type === 'VoiceDialog') && (!exercise.dialog || !exercise.meta)) {
      if (exercise.exerciseData && (exercise.exerciseData.dialog || exercise.exerciseData.meta)) {
        exercise = {
          ...exercise,
          type: 'Dialog',
          dialog: exercise.dialog || exercise.exerciseData.dialog || [],
          meta: exercise.meta || exercise.exerciseData.meta || {}
        };
      }
    }
    // Check if this is a dialog exercise that needs transformation
    if (exercise.questions && exercise.questions.some(q => q.type === 'Dialog')) {
      const transformedQuestions = exercise.questions.map(question => {
        if (question.type !== 'Dialog') {
          return question;
        }

        const dialogData = (question as any).dialogData;
        if (!dialogData) {
          console.error('[ExerciseService] Dialog question is missing dialogData and cannot be rendered.', {
            exerciseId: exercise._id,
            questionId: (question as any)?._id
          });
          return question;
        }

        return {
          ...question,
          question: dialogData,
          dialogData
        } as IExerciseQuestionType;
      });

      return new Exercise({
        ...exercise,
        questions: transformedQuestions
      });
    }

    // PRIORITY 1: New backend format - canonical dialog object stored in `exercise.dialog`
    if ((exercise.type === 'dialog' || exercise.type === 'Dialog') &&
        exercise.dialog && !Array.isArray(exercise.dialog) && Array.isArray((exercise.dialog as any).messages)) {
      console.log('Transforming dialog exercise from canonical dialog object:', exercise._id);
      const source = exercise.dialog as any;

      const dialogData: DialogData = {
        title: source.title || exercise.title || 'Dialog Exercise',
        level: source.level || exercise.level || 'A1',
        language: source.language || 'de',
        messages: source.messages || [],
        quizzes: source.quizzes || []
      };

      const dialogQuestion = new DialogExerciseQuestion(
        exercise._id,
        dialogData,
        -1,
        '',
        this.mapDifficultyLevel(dialogData.level),
        exercise.tags || [],
        undefined,
        exercise.task
      );

      const mappedExercise = new Exercise({
        _id: exercise._id,
        title: dialogData.title,
        description: exercise.description || '',
        questions: [dialogQuestion],
        tags: exercise.tags || [],
        maxProgress: dialogData.quizzes.length,
        difficulty: this.mapDifficultyLevel(dialogData.level),
        author: exercise.author
      });

      // Preserve canonical dialog data for UI under dialogData for now
      (mappedExercise as any).dialogData = dialogData;
      (mappedExercise as any).level = dialogData.level;
      return mappedExercise;
    }

    // PRIORITY 2: Older canonical format where backend exposes dialogData separately
    if ((exercise.type === 'dialog' || exercise.type === 'Dialog') && exercise.dialogData) {
      console.log('Transforming dialog exercise from canonical dialogData:', exercise._id);
      console.log('DialogData:', exercise.dialogData);
      
      const dialogData: DialogData = {
        title: exercise.dialogData.title || exercise.title || 'Dialog Exercise',
        level: exercise.dialogData.level || exercise.level || 'A1',
        language: exercise.dialogData.language || 'de',
        messages: exercise.dialogData.messages || [],
        quizzes: exercise.dialogData.quizzes || []
      };
      
      const dialogQuestion = new DialogExerciseQuestion(
        exercise._id,
        dialogData,
        -1,
        '',
        this.mapDifficultyLevel(dialogData.level),
        exercise.tags || [],
        undefined,
        exercise.task
      );
      
      const mappedExercise = new Exercise({
        _id: exercise._id,
        title: dialogData.title,
        description: exercise.description || '',
        questions: [dialogQuestion],
        tags: exercise.tags || [],
        maxProgress: dialogData.quizzes.length,
        difficulty: this.mapDifficultyLevel(dialogData.level),
        author: exercise.author
      });
      (mappedExercise as any).dialogData = dialogData;
      (mappedExercise as any).level = dialogData.level;
      return mappedExercise;
    }

    // No transformation needed for other exercise types
    return new Exercise(exercise);
  }
}